import React, { PureComponent, lazy, Suspense } from 'react';
import { Router, Route, Switch } from 'react-router-dom';
import { NotificationProvider } from 'day8-react/build/new-components';
import LoadingView from 'day8-react/build/LoadingView';
import * as Sentry from "@sentry/react";
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

import history from '../history';
import YachtBooking from '../containers/booking/YachtBooking';
import StartBooking from '../containers/booking/StartBooking';
import PublicArrivalInfo from '../containers/PublicArrivalInfo';
import StartCabinBooking from '../containers/booking/StartCabinBooking';
import LoginModal from '../containers/auth/LoginModal';
import StartProductBooking from '../containers/booking/StartProductBooking';
import PlanStart from '../containers/PlanStart';
import { getQuery } from '../helpers/querystring';
import { onMarketingConsent } from '../helpers/consent';
import { captureAttribution } from '../helpers/attribution';
import { EmailSignupProvider } from '../context/EmailSignupContext';
import { WaitingListContext, WaitingListProvider } from '../context/WaitingListContext';
import { AutomaticDiscountsProvider } from '../context/AutomaticDiscountsContext';
import { PlannerProvider } from '../context/PlannerContext';
import WaitingListModal from '../components/WaitingList/WaitingListModal';
import BookingNavbar from '../components/booking-navbar/BookingNavbar';
import Footer from '../components/account/footer/Footer';
import { CabinCrewModalContext, CabinCrewModalProvider } from '../context/CabinCrewModalContext';
import { BookingBasketProvider } from '../context/BookingBasketContext';
import { MobileAppNavProvider } from '../context/MobileAppNavContext';
import BookingFooterWrapper from '../components/BookingFooterWrapper';
import { InfoDrawerProvider } from '../context/InfoDrawerContext';
import { StickyFooterProvider } from '../context/StickyFooterContext';
import InfoDrawer from '../components/InfoDrawer/InfoDrawer';
import { InvitationModalProvider } from '../context/InvitationModalContext';
import { EditProfilePictureModalProvider } from '../context/EditProfilePictureModalContext';
import { ProfileEditModalProvider } from '../context/ProfileEditModalContext';
import { withActiveCompany } from '../context/ActiveCompanyContext';
import InvitationModal from '../components/InvitationModal/InvitationModal';
import EditProfilePictureModal from '../components/account/edit/EditProfilePictureModal';
import ProfileEditModal from '../components/account/edit/ProfileEditModal';
import CabinCrewModal from '../components/booking-options/CabinCrewModal';
import { isAmbassadorPortalRoute } from '../helpers/ambassadorPortal';

const YachtList = lazy(() => import('../containers/YachtList'));
const YachtsAndFriendsList = lazy(() => import('../containers/ynf/YachtsAndFriendsList'));
const Planner = lazy(() => import('../containers/Planner'));
const Invitation = lazy(() => import('../containers/booking/Invitation'));
const AccountRouting = lazy(() => import('./AccountRouting'));

class Routing extends PureComponent {
  static configureSentry(user) {
    if (user) {
      Sentry.setUser({
        email: user.email,
        id: user.id
      });
    }
  }

  constructor(props) {
    super(props);
    if (props && props.user) {
      Routing.configureSentry(props.user);
    }
  }

  componentDidMount() {
    // Add route change listeners
    history.listen(() => {
      NProgress.start();
      // Small delay to make progress bar visible even on fast page loads
      setTimeout(() => {
        NProgress.done();
      }, 300);
    });
    const { tywRef } = getQuery(window.location);
    if (tywRef) {
      Cookies.set('tyw_affiliate_code', tywRef, { expires: 90 });
    }
    // Marketing attribution (UTM / gclid / gbraid / wbraid / fbclid) — gated on Cookiebot marketing consent
    this._detachConsent = onMarketingConsent(() => captureAttribution());
  }

  componentDidUpdate(prevProps) {
    if (
      (!prevProps.user && this.props.user) ||
      (prevProps.user && this.props.user && prevProps.user.id !== this.props.user.id)
    ) {
      Routing.configureSentry(this.props.user);
    }
  }

  componentWillUnmount() {
    if (this._detachConsent) {
      this._detachConsent();
    }
  }

  static getBookingFirewallRoutes() {
    return (
      <>
        <Switch>
          <Route path="/booking/start/:boatWeekId" component={StartBooking} />
          <Route path="/booking/yachtsandfriends/start/:boatWeekId" render={routeProps => <StartBooking {...routeProps} isYachtsAndFriends />} />
          <Route path="/booking/standard-start/:standardYachtWeekId" component={StartProductBooking} />
          <Route path="/booking/yachtsandfriends/standard-start/:standardYachtWeekId" render={routeProps => <StartProductBooking {...routeProps} isYachtsAndFriends />} />
          <Route path="/booking/yachtsandfriends" render={routeProps => <YachtBooking {...routeProps} isYachtsAndFriends />} />
          <Route path="/booking" component={YachtBooking} />
        </Switch>
        <Route path="/cabin-booking/start/:cabinWeekId" component={StartCabinBooking} />
        <Route path="/cabin-booking/yachtsandfriends/start/:cabinWeekId" render={routeProps => <StartCabinBooking {...routeProps} isYachtsAndFriends />} />
      </>
    );
  }

  render() {
    const {
      user, userLoaded
    } = this.props;
    // Hardcoding now until decided where we want footer
    // const noFooterPaths = ['/browse', '/booking', '/cabin-booking'];
    // const shouldShowFooter = !noFooterPaths.some(path => history.location.pathname.startsWith(path));
    const shouldShowFooter = false;

    return (
      <StickyFooterProvider>
        <NotificationProvider>
          <EmailSignupProvider>
            <InvitationModalProvider>
              <EditProfilePictureModalProvider>
                <ProfileEditModalProvider>
                  <CabinCrewModalProvider>
                    <WaitingListProvider>
                      <AutomaticDiscountsProvider>
                        <PlannerProvider>
                          <BookingBasketProvider>
                            <MobileAppNavProvider>
                              <Suspense fallback={<LoadingView />}>
                                <Router history={history}>
                                  {/* InfoDrawerProvider is using react router links so needs to be inside the router */}
                                  <InfoDrawerProvider>
                                    <div className='dui-flex dui-flex-col dui-justify-between dui-min-h-screen'>
                                      <div>
                                        {/* The ambassador portal is its own standalone page — no "Call us" bar, same as it
                                            has no account sidebar (see AmbassadorPortalLayout.js). A bare <Route> (no
                                            `path`) always matches, so this stays reactive to client-side navigation
                                            without needing Routing itself to sit inside the Router's location context. */}
                                        <Route render={({ location }) => (
                                          !isAmbassadorPortalRoute(location.pathname) && (
                                            <BookingNavbar key={JSON.stringify(user)} isYachtsAndFriends={this.props.isYachtsAndFriends} />
                                          )
                                        )} />
                                        <Switch>
                                          <Route path="/browse/yachtsandfriends" component={YachtsAndFriendsList} />
                                          <Route path="/browse" component={YachtList} />
                                        </Switch>
                                        {userLoaded && Routing.getBookingFirewallRoutes()}
                                        <Route path="/account" component={AccountRouting} />
                                        <Route exact path="/plan/" component={PlanStart} />
                                        <Route exact path="/invitation/:bookingId" component={Invitation} />
                                        <Route exact path="/plan/:planCode" component={Planner} />
                                        <Route path="/arrival-info/:weekIdHash" component={PublicArrivalInfo} />
                                        <Route path="/" component={LoginModal} />
                                        <Route path="/" />
                                        {shouldShowFooter && <Footer />}
                                      </div>
                                      <BookingFooterWrapper isYachtsAndFriends={this.props.isYachtsAndFriends} />
                                    </div>
                                    <InfoDrawer />
                                    <CabinCrewModalContext.Consumer>
                                      {cabinCrewModalContext => <CabinCrewModal {...cabinCrewModalContext.modalProps} open={cabinCrewModalContext.isModalOpen} onClose={cabinCrewModalContext.close} />}
                                    </CabinCrewModalContext.Consumer>
                                  </InfoDrawerProvider>
                                </Router>
                                <InvitationModal />
                                <WaitingListContext.Consumer>
                                  {waitingListContext => <WaitingListModal on={waitingListContext.isModalOpen} waitingListInfo={waitingListContext.modalProps} toggle={waitingListContext.close}/>}
                                </WaitingListContext.Consumer>
                                <ProfileEditModal />
                                <EditProfilePictureModal />
                              </Suspense>
                            </MobileAppNavProvider>
                          </BookingBasketProvider>
                        </PlannerProvider>
                      </AutomaticDiscountsProvider>
                    </WaitingListProvider>
                  </CabinCrewModalProvider>
                </ProfileEditModalProvider>
              </EditProfilePictureModalProvider>
            </InvitationModalProvider>
          </EmailSignupProvider>
        </NotificationProvider>
      </StickyFooterProvider>
    );
  }
}

const select = store => ({
  user: store.user.loggedUser,
  userLoaded: !!store.user.loggedUser || !store.user.fetching,
  booking: store.booking.currentBooking,
  destinations: store.destinations.items,
  account: store.account
});

Routing.propTypes = {
  user: PropTypes.object,
  userLoaded: PropTypes.bool,
  booking: PropTypes.object,
  destinations: PropTypes.array,
  account: PropTypes.object
};

export default withActiveCompany(connect(select)(Routing));
